/** True when value is a usable http(s) recording URL (not blank / not_available). */
export function isValidRecordingUrl(url) {
  if (!url || typeof url !== 'string') return false
  const trimmed = url.trim()
  if (!trimmed || trimmed.toLowerCase() === 'not_available') return false
  return /^https?:\/\//i.test(trimmed)
}

/** S3-hosted URLs only (presigned or direct S3 object URL) — excludes Indus/Exotel vendor links. */
export function pickS3RecordingUrl(call = {}) {
  const candidates = [
    call.RecordingPresignedUrl,
    call.recording_presigned_url,
    call.recordingPresignedUrl,
    call.RecordingS3Url,
    call.recording_s3_url,
    call.recordingS3Url,
  ]
  for (const url of candidates) {
    if (isValidRecordingUrl(url)) return url.trim()
  }
  return null
}

/**
 * Vendor recording URL (Indus Labs webhook / Exotel RecordingUrl).
 * Used only when no S3 key exists.
 */
export function pickVendorRecordingUrl(call = {}) {
  const candidates = [
    call.recordingUrl,
    call.RecordingUrl,
    call.recording_url,
  ]
  for (const url of candidates) {
    if (isValidRecordingUrl(url)) return url.trim()
  }
  return null
}

/** @deprecated Prefer pickS3RecordingUrl + pickVendorRecordingUrl via resolveRecordingAccess. */
export function pickRecordingUrl(call = {}) {
  return pickS3RecordingUrl(call) || pickVendorRecordingUrl(call)
}

/** Extract S3 object key only (never a full URL). */
export function pickRecordingS3Key(call = {}, rawPayload = {}) {
  const webhook = rawPayload?.webhook_response?.data || rawPayload?.webhook_response || {}
  const candidates = [
    call.recordingS3Key,
    call.recording_s3_key,
    call.RecordingS3Key,
    webhook.recording_key,
  ]
  for (const key of candidates) {
    if (!key || typeof key !== 'string') continue
    const trimmed = key.trim()
    if (!trimmed || /^https?:\/\//i.test(trimmed)) continue
    return trimmed
  }
  return null
}

const NO_RECORDING_STATUSES = new Set([
  'no-answer', 'no_answer', 'noanswered', 'noanswer', 'not answered', 'notanswered',
  'failed', 'busy', 'canceled', 'cancelled',
])

function isNoRecordingStatus(item = {}) {
  const raw = String(item._rawStatus || item.status || '').toLowerCase()
  if (NO_RECORDING_STATUSES.has(raw)) return true
  const compact = raw.replace(/[_-]/g, ' ')
  return compact === 'not answered' || compact === 'no answer'
}

/** True when a stored recording field is the API's explicit "not_available" marker. */
export function isExplicitlyUnavailableRecording(call = {}, rawPayload = {}) {
  const webhook = rawPayload?.webhook_response?.data || rawPayload?.webhook_response || {}
  const candidates = [
    call.recordingUrl,
    call.RecordingUrl,
    call.recording_url,
    call.RecordingPresignedUrl,
    call.recording_presigned_url,
    call.recordingPresignedUrl,
    webhook.recording,
    webhook.RecordingUrl,
    webhook.recording_url,
  ]
  return candidates.some(url => typeof url === 'string' && url.trim().toLowerCase() === 'not_available')
}

function hasExplicitUnavailableUrl(item = {}) {
  return isExplicitlyUnavailableRecording(item)
}

/** Whether the row might have a recording (S3 key, vendor URL, or an answered sid we can resolve). */
export function hasRecordingAvailable(item = {}) {
  if (hasExplicitUnavailableUrl(item)) return false
  if (item.recordingS3Key || pickS3RecordingUrl(item) || pickVendorRecordingUrl(item)) return true
  if (isNoRecordingStatus(item)) return false
  return Boolean(item.sid || item.callDbId)
}
